import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { forkJoin, Subscription } from 'rxjs';
import { DictionaryAPIService } from 'src/app/extensions/services/api/dictionary-api.service';
import { RequestAPIService } from 'src/app/extensions/services/api/request-api.service';
import { UserAPIService } from 'src/app/extensions/services/api/user-api.service';
import { ScrollService } from 'src/app/extensions/services/scroll.service';
import { isEqual } from 'lodash';
import { scrollTop } from 'src/app/extensions/utilities';
import { RequestListFilterComponent } from 'src/app/features/my-requests/request-list/request-list-filter/request-list-filter.component';
import { CONTRACT_MODIFICATIONS_WRITE_RIGHTS } from 'src/app/extensions/constants/right-constants';
import { DictionaryRequestStatus } from 'src/app/extensions/constants/dictionaryConstants/dictionaryConstants';

@Component({
  selector: 'app-contract-modification-list',
  templateUrl: './contract-modification-list.component.html',
  styleUrls: ['./contract-modification-list.component.scss'],
  providers: [ScrollService],
  standalone: false,
})
export class ContractModificationListComponent implements OnInit, OnDestroy {
  contractModifications: app.models.ContractModificationRequest[] = [];
  contractModificationsProps: app.models.DynamicPropsDictionary;
  public hasData = true;
  public hasFilterData = true;

  private pageIndex = 1;
  private maxIndex: number;
  pageFilter: app.models.GetContractModificationRequestsModelPageFilter;
  public initialFilterState: app.models.GetRequestsModelPageFilter;
  public activeFilterCount = 0;

  contractModificationRequestType: app.models.DictionaryDataModel[];
  REIMBURSEMENT: app.models.DictionaryDataModel;

  userRights: string[];
  readOnly: boolean = true;

  public subscriptions: Subscription[] = [];
  public showBackToTop: boolean = false;
  public modalRequestsStatuses: app.models.DictionaryDataModel[] = [];
  public isFiltered: boolean = false;

  constructor(
    private _router: Router,
    private _requestAPIService: RequestAPIService,
    private _dictionaryAPIService: DictionaryAPIService,
    private _userAPIService: UserAPIService,
    private _ngbModal: NgbModal,
    private _scrollService: ScrollService
  ) {}

  ngOnInit(): void {
    this.pageFilter = {
      statusesIds: [],
    } as app.models.GetRequestsModelPageFilter;
    this.initialFilterState = structuredClone(this.pageFilter);
    let dataSource = forkJoin([
      this._dictionaryAPIService.getDictionaryContractModificationRequestType(),
      this._userAPIService.getUserRights(),
      this._dictionaryAPIService.getDictionaryRequestStatuses(),
    ]);

    let dataSourceSub = dataSource.subscribe(
      ([dictionaryContractModificaitonRequestTypes, userRights, requestStatuses]) => {
        this.contractModificationRequestType = dictionaryContractModificaitonRequestTypes;
        this.REIMBURSEMENT = dictionaryContractModificaitonRequestTypes.find(x => x.code == 'RQST_REIMBURSEMENT');

        this.userRights = userRights.rights.map(x => x.code);
        this.readOnly = !this.userRights.some(userRight => CONTRACT_MODIFICATIONS_WRITE_RIGHTS.includes(userRight));
        this.modalRequestsStatuses = requestStatuses.data;

        this.pageFilter.statusesIds = this.modalRequestsStatuses
          .filter(status => status.code !== DictionaryRequestStatus.CLOSED)
          .map(status => status.id);

        this.initialFilterState = structuredClone(this.pageFilter);
        this.activeFilterCount = this.pageFilter.statusesIds.length;
        this.loadData();
      }
    );
    this.subscriptions.push(dataSourceSub);

    let backToTopSubscription = this._scrollService.obsShowBackToTop.subscribe(x => {
      this.showBackToTop = x;
    });
    this.subscriptions.push(backToTopSubscription);

    this._scrollService.obsFetchData.subscribe(() => {
      if (this.pageIndex <= this.maxIndex) {
        this.pageIndex++;
        this._requestAPIService.getContractModificationRequests(this.pageIndex, this.pageFilter).subscribe(result => {
          this.contractModifications = [...this.contractModifications, ...result.data.values];
          this.maxIndex = result.data.totalPages;
          if (this.pageIndex <= this.maxIndex) {
            this._scrollService.waitingForDataFetch = false;
          }
        });
      }
    });
  }

  ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.forEach(subscription => {
        subscription.unsubscribe();
      });
    }
  }

  loadData(filterMode: boolean = false) {
    this._requestAPIService.getContractModificationRequests(this.pageIndex, this.pageFilter).subscribe(result => {
      this.contractModifications = result.data.values;
      this.contractModificationsProps = result.props;
      this.maxIndex = result.data.totalPages;
      this.isFiltered = filterMode;
      if (filterMode) {
        this.hasFilterData = this.contractModifications.length > 0;
      } else {
        this.hasData = this.contractModifications.length > 0;
      }

      this._scrollService.waitingForDataFetch = this.pageIndex > this.maxIndex;
    });
  }

  public filterHandler() {
    let modal = this._ngbModal.open(RequestListFilterComponent, { backdrop: 'static' });
    modal.componentInstance.modalFilters = this.pageFilter;
    modal.componentInstance.initialFilterState = this.initialFilterState;
    modal.componentInstance.modalRequestsStatuses = this.modalRequestsStatuses;

    modal.closed.subscribe(result => {
      if (result !== false) {
        this.initialFilterState = {
          ...this.initialFilterState,
          statusesIds: this.pageFilter.statusesIds,
        };
        this.activeFilterCount = result;
        this.pageIndex = 1;
        if (!isEqual(this.pageFilter, this.initialFilterState)) {
          this.pageFilter = structuredClone(this.initialFilterState);
        }

        this.loadData(true);
      }
    });
  }

  public resetFiltersHandler() {
    this.activeFilterCount = 0;

    this.initialFilterState = {
      ...this.initialFilterState,
      statusesIds: this.pageFilter.statusesIds,
    };

    if (!isEqual(this.pageFilter, this.initialFilterState)) {
      this.pageFilter = structuredClone(this.initialFilterState);
    }

    this.pageFilter = {
      statusesIds: [],
    } as app.models.GetRequestsModelPageFilter;

    this.pageIndex = 1;
    this.hasFilterData = true;
    this.loadData();
  }

  viewDetailsHandler = (contractModificationId: string) => {
    this._router.navigate(['requests/contract-modifications', contractModificationId]);
  };

  redirectNewRequestHandler() {
    this._router.navigate(['requests/contract-modifications/new']);
  }

  redirectRequestTypeHandler() {
    this._router.navigate(['requests/entry']);
  }

  scrollToTop(): void {
    scrollTop();
  }
}
